import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { Portal } from 'react-native-paper'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import UniActivityIndicator from '@/components/unistyles/UniActivityIndicator'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'

const GlobalTaskIndicator = () => {
  const { theme } = useUnistyles()
  const globalTasks = useAppStore.use.globalTasks()
  const [shouldRender, setShouldRender] = useState(false)

  // Get the most recent active task
  const activeTask =
    globalTasks.activeTasks.length > 0
      ? globalTasks.tasks[
          globalTasks.activeTasks[globalTasks.activeTasks.length - 1]
        ]
      : null

  // Animation values
  const opacity = useSharedValue(0)
  const translateY = useSharedValue(-50)

  const hideComponent = () => {
    setShouldRender(false)
  }

  useEffect(() => {
    if (activeTask) {
      setShouldRender(true)
      // Animate in
      opacity.value = withTiming(1, { duration: 300 })
      translateY.value = withSpring(0, { damping: 15, stiffness: 150 })
    } else {
      // Animate out
      opacity.value = withTiming(0, { duration: 200 })
      translateY.value = withTiming(-50, { duration: 200 }, () => {
        runOnJS(hideComponent)()
      })
    }
  }, [activeTask, globalTasks, opacity, translateY])

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    }
  })

  if (!shouldRender) {
    return null
  }

  return (
    <Portal>
      <Animated.View style={[styles.container, animatedStyle]}>
        <View style={styles.content}>
          <UniActivityIndicator
            animating={true}
            color={theme.colors.primary}
            size="small"
          />
          <View style={styles.textContainer}>
            <UniText style={styles.title} variant="bodyMedium">
              {activeTask?.title}
            </UniText>
            {activeTask?.description && (
              <UniText style={styles.description} variant="bodySmall">
                {activeTask.description}
              </UniText>
            )}
          </View>
          {activeTask?.progress !== undefined && (
            <UniText style={styles.progress} variant="bodySmall">
              {Math.round(activeTask.progress)}%
            </UniText>
          )}
        </View>
      </Animated.View>
    </Portal>
  )
}

const styles = StyleSheet.create((theme) => ({
  container: {
    elevation: 20,
    left: {
      xs: '5%',
      sm: 'auto',
    },
    maxWidth: 400,
    minWidth: 300,
    position: 'absolute',
    right: 16,
    top: 50,
    width: {
      xs: '90%',
      sm: 400,
    },
    zIndex: 9999,
  },
  content: {
    alignItems: 'center',
    backgroundColor: theme.colors.surfaceVariant,
    borderColor: theme.colors.outline,
    borderRadius: theme.tokens.containerBorderRadius,
    borderWidth: 1,
    elevation: 5,
    flexDirection: 'row',
    gap: theme.tokens.spacing[3],
    padding: theme.tokens.spacing[4],
    shadowColor: theme.colors.shadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  description: {
    color: theme.colors.onSurfaceVariant,
    marginTop: 2,
  },
  progress: {
    color: theme.colors.onSurfaceVariant,
    fontWeight: '500',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontWeight: '500',
  },
}))

export default GlobalTaskIndicator
